
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  TextField,
  Link,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { userLogin, fetchCurrentUser } from "../../redux/usersSlice";
import Captcha from "./Captcha";
import leftBg from "../../assets/icons/landing-left-bg.png"; 

/* ────────────────────────────────────────────────────────────────────────── */
/*  🎨  Design tokens (identiques à LandingPage)                             */
/* ────────────────────────────────────────────────────────────────────────── */
const NAVY       = "#2F4E8B";
const NAVY_LIGHT = "#4a6bad";
const OUTLINE    = "#D4D9E0";
const LABEL_GREY = "#4F5565";

const CustomTextField = styled(TextField)(() => ({
  "& .MuiInput-underline:before": { borderBottomColor: OUTLINE },
  "& .MuiInput-underline:hover:before": { borderBottomColor: NAVY_LIGHT },
  "& .MuiInput-underline:after": { borderBottomColor: NAVY },
  "& .MuiFormLabel-root": { color: LABEL_GREY, fontSize: 14 },
  "& .MuiFormLabel-root.Mui-focused": { color: NAVY },
  "& .MuiInputBase-input": { fontSize: 16, padding: "6px 0" },
}));

const scrollbar = {
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { background: "#e6e8f0", borderRadius: 4 },
  "&::-webkit-scrollbar-thumb": { background: NAVY, borderRadius: 4 },
  scrollbarWidth: "thin",
  scrollbarColor: `${NAVY} #e6e8f0`,
};

const SignIn = () => {
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [formValues, setFormValues]   = useState(null);

  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { token, loading, error, currentUser } = useSelector((s) => s.users);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormValues({ email, password });
    setCaptchaOpen(true);
  };

  const handleCaptchaSuccess = (captchaVerification) => {
    setCaptchaOpen(false);
    dispatch(userLogin({ ...formValues, captchaVerification }));
  };

  useEffect(() => {
    if (token && !currentUser) dispatch(fetchCurrentUser());
  }, [token, currentUser, dispatch]);

  useEffect(() => {
    if (!currentUser) return;
    if (["creation", "chgPwd"].includes(currentUser.status)) navigate("/2fa");
    else if (currentUser.status === "in use") navigate("/acceuil");
  }, [currentUser, navigate]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        // utilisation du même background que la Landing Page
        background: `url(${leftBg}) no-repeat center / cover`,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: { xs: "100%", sm: 420, md: 500 },
          borderRadius: 3,
          p: { xs: 4, sm: 5 },
          position: "relative",
          border: `2px solid ${NAVY}`,
          backgroundColor: "rgba(255,255,255,0.85)", // légère opacité pour lisibilité
        }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{ fontWeight: 800, mb: 1, color: NAVY }}
        >
          Bienvenue sur Tallentum
        </Typography>
        <Typography
          variant="body2"
          align="center"
          sx={{ color: "#6b6b6b", mb: 4 }}
        >
          Veuillez vous connecter pour continuer.
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <CustomTextField
            variant="standard"
            label="Adresse email"
            type="email"
            fullWidth
            required
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <CustomTextField
            variant="standard"
            label="Mot de passe"
            type="password"
            fullWidth
            required
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: 1,
              mb: 2,
            }}
          >
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="hover"
              sx={{ color: NAVY, fontWeight: 600 }}
              onClick={() => navigate("/envoyer-lien")}
            >
              Mot de passe oublié&nbsp;?
            </Link>
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              backgroundColor: NAVY,
              fontWeight: 700,
              py: 1.2,
              mb: 2,
              "&:hover": { backgroundColor: NAVY_LIGHT },
            }}
          >
            {loading ? "Connexion..." : "Connexion"}
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
      </Paper>

      {captchaOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.35)",
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              width: { xs: "95%", sm: "90%", md: "80%" },
              maxHeight: { xs: "95vh", sm: "90vh" },
              overflowY: "auto",
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              border: `2px solid ${NAVY}`,
              ...scrollbar,
            }}
          >
            <Captcha
              onSuccess={handleCaptchaSuccess}
              handleClose={() => setCaptchaOpen(false)}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SignIn;
